import { useEffect, useRef, useState } from "react";
import {
  ChallengeImage,
  ChallengeShell,
  useChoices,
  type ChallengeProps,
} from "./shared";
import { speak } from "../../audio";
import { viAudioUrl } from "../../../local/languageSwap";
import type { WordDTO } from "@shared/types";

/**
 * See a picture, listen to 4 audio bubbles, pick the one that matches.
 * Tap-once = play + select. Tap the SAME bubble again = commit. On a wrong
 * pick we hold a teaching beat (English once, then Vietnamese) so the
 * student learns the meaning — then the question re-presents with the
 * bubbles reshuffled and the student must answer correctly to advance.
 */
export function ImageToAudio({ word, distractors, onAnswer }: ChallengeProps) {
  const [retryKey, setRetryKey] = useState(0);
  const choices = useChoices(word, distractors, retryKey);
  const [selected, setSelected] = useState<WordDTO | null>(null);
  const [committed, setCommitted] = useState<string | null>(null);
  const [teaching, setTeaching] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, []);

  const later = (fn: () => void, ms: number) => {
    const timer = window.setTimeout(fn, ms);
    timers.current.push(timer);
  };

  const tap = (c: WordDTO) => {
    if (committed || teaching) return;
    if (selected?.id === c.id) {
      setCommitted(c.id);
      const correct = c.id === word.id;
      if (correct) {
        later(() => onAnswer(true), 800);
      } else {
        // Wrong → teach English then Vietnamese, then reshuffle and let the
        // student try again. They must get it right before advancing.
        later(() => setTeaching(true), 400);
        later(() => speak(word.english, { audioUrl: word.audioUrl, lang: "en-US" }), 700);
        if (word.vietnamese && word.vietnamese.trim()) {
          later(() => speak(word.vietnamese, { lang: "vi-VN", audioUrl: viAudioUrl(word.vietnamese) }), 2400);
        }
        later(() => {
          setTeaching(false);
          setCommitted(null);
          setSelected(null);
          setRetryKey((k) => k + 1);
        }, 4400);
      }
      return;
    }
    setSelected(c);
    speak(c.english, { audioUrl: c.audioUrl, lang: "en-US" });
  };

  return (
    <>
      <ChallengeShell
        tag="Picture sound"
        imageUrl={word.imageUrl}
        imageLabel={word.english}
        prompt={
          selected && !committed
            ? "Tap the same bubble again to choose it"
            : "Tap a bubble to hear it"
        }
      >
        <div className="audio-bubbles">
          {choices.map((c, i) => {
            const correct = c.id === word.id;
            const isSelected = selected?.id === c.id;
            const isCommitted = committed === c.id;
            let cls = "";
            if (committed) {
              cls = isCommitted
                ? correct
                  ? "good"
                  : "bad"
                : correct
                  ? "good faded"
                  : "faded";
            } else if (isSelected) {
              cls = "selected-pick";
            }
            return (
              <button
                key={c.id}
                className={`audio-bubble ${cls}`}
                onClick={() => tap(c)}
                disabled={!!committed || teaching}
                aria-label={`Sound ${i + 1}`}
              >
                <span className="audio-bubble-icon">🔊</span>
                <span className="audio-bubble-num">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </ChallengeShell>
      {teaching && (
        <div className="teach-overlay">
          <div className="teach-card">
            <p className="muted teach-label">The answer was…</p>
            {word.imageUrl && (
              <ChallengeImage className="teach-image" src={word.imageUrl} alt={word.english} />
            )}
            <h2 className="teach-english">{word.english}</h2>
            <div className="teach-progress" />
          </div>
        </div>
      )}
    </>
  );
}
